import { observer } from 'mobx-react';
import { Component } from 'react';
import { withOrderStore } from '../../context';
import CancelDeliveryWarningModal from './components/CancelDeliveryWarningModal/CancelDeliveryWarningModal';
import { BUTTON_TITLE } from 'src/constants/main';
import { ORDERS_GROUPS, ORDERS_TYPES_GROUP, ORDER_TYPE } from 'src/constants/order';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION, TOOLTIP_TRIGGERS } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import ModalMessage from 'src/view/components/ModalMessage/ModalMessage';
import PopupModal from 'src/view/components/Popup/Popup';
import { DELIVERY_REQUEST_STATUS } from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/constants/deliveryTracking';
import { OrderStore } from '~/mobx/orderStore';
import logger from '~/services/logger';
import { IMessageModalOption, messagesStore } from '~/view/containers/Messages';

interface State {
  cancelDeliveryModal: boolean;
  requestDeliveryModal: boolean;
  cancelDeliveryWarningModal: boolean;
  requestErrorMessage: string | undefined;
  //
  requestingDelivery: boolean;
  cancelingDelivery: boolean;
}

// @ts-ignore
@withOrderStore
@observer
export default class OrderRequestDelivery extends Component<{ orderStore?: OrderStore }, State> {
  state = {
    cancelDeliveryModal: false,
    requestDeliveryModal: false,
    cancelDeliveryWarningModal: false,
    requestErrorMessage: undefined,
    //
    requestingDelivery: false,
    cancelingDelivery: false,
  };

  get orderStore() {
    return this.props.orderStore!;
  }

  cancelDeliveryWarningModal = (state: boolean) => {
    if (state) {
      this.setState({ cancelDeliveryWarningModal: true }, () => this.setState({ cancelDeliveryModal: false }));
    } else {
      this.setState({ cancelDeliveryWarningModal: false });
    }
  };

  requestDelivery = async () => {
    try {
      if (!this.orderStore.order) return null;

      this.setState({ requestingDelivery: true });
      const response = await this.orderStore.requestDelivery(this.orderStore.order.id);
      messagesStore.addMessage({
        title: localeStore.t('txt_send_manually'),
        message: response.message,
      });
      this.setState({ requestingDelivery: false }, () => this.setState({ requestDeliveryModal: false }));
    } catch (error: any) {
      this.setState({ requestingDelivery: false }, () => this.setState({ requestDeliveryModal: false }));

      if (error.response?.data?.type === 'request_resend_restriction') {
        this.setState({ requestErrorMessage: error.response?.data?.message || error.message });
        return;
      }

      logger.error({ error, place: 'OrderRequestDelivery requestDelivery', showModal: true });
    }
  };

  cancelDelivery = async () => {
    if (!this.orderStore.order) return null;
    try {
      this.setState({ cancelingDelivery: true });
      const response = await this.orderStore.canсelDelivery(this.orderStore.order.id);
      messagesStore.addMessage({
        title: localeStore.t('cancel_delivery'),
        message: response.message,
      });
      this.setState({ cancelingDelivery: false }, () => this.setState({ cancelDeliveryModal: false }));
    } catch (error: any) {
      this.setState({ cancelingDelivery: false }, () => this.cancelDeliveryWarningModal(true));
      logger.error({ error, place: 'OrderRequestDelivery cancelDelivery' });
    }
  };

  get disabled() {
    return this.orderStore.order && ORDERS_TYPES_GROUP[ORDER_TYPE.HISTORY][ORDERS_GROUPS.ALL].includes(this.orderStore.order.order_status_code);
  }

  get requestDeliveryMessageOptions(): IMessageModalOption[] {
    return [
      { title: localeStore.t(BUTTON_TITLE.NO), onPress: () => this.setState({ requestDeliveryModal: false }), appearance: 'tertiary' },
      { title: localeStore.t(BUTTON_TITLE.YES), onPress: this.requestDelivery, loading: this.state.requestingDelivery },
    ];
  }

  get cancelDeliveryMessageOptions(): IMessageModalOption[] {
    return [
      { title: localeStore.t(BUTTON_TITLE.NO), onPress: () => this.setState({ cancelDeliveryModal: false }), appearance: 'tertiary' },
      { title: localeStore.t(BUTTON_TITLE.YES), onPress: this.cancelDelivery, loading: this.state.cancelingDelivery },
    ];
  }

  render() {
    if (!appStore.settings?.takergo_enabled || this.disabled || !this.orderStore.order) return null;

    const isRequestDeliveryVisible =
      !appStore.settings.on_takergo_assist ||
      this.orderStore.order.takergo?.status === undefined ||
      [DELIVERY_REQUEST_STATUS.CANCELED].includes(+this.orderStore.order.takergo?.taker_go_status);
    const isCancelDeliveryVisible = !appStore.settings.on_takergo_assist;

    return (
      <div className="d-flex flex-row mb-2 gap-10">
        {isRequestDeliveryVisible && (
          <PopupModal
            direction="left"
            justify="end"
            selfClose={false}
            backdrop
            onClose={() => this.setState({ requestDeliveryModal: false })}
            visible={this.state.requestDeliveryModal}
            content={({ closeModal }) => (
              <ModalMessage
                minWidth={300}
                title={localeStore.t('send_manually')}
                closeModal={closeModal}
                iconClassName="color-gray2"
                options={this.requestDeliveryMessageOptions}
              />
            )}
          >
            <div className="pos-relative">
              <CustomTooltip
                trigger={Boolean(this.state.requestErrorMessage)}
                duration={TOOLTIP_DURATION.EXTRA_LARGE}
                position={TOOLTIP_POSITION.FIXED}
                disableTrigger={() => this.setState({ requestErrorMessage: undefined })}
                triggerType={TOOLTIP_TRIGGERS.NONE}
                message={this.state.requestErrorMessage}
              >
                <Button
                  title={localeStore.t('txt_send_manually')}
                  onClick={() => this.setState({ requestDeliveryModal: true })}
                  loading={this.state.requestingDelivery}
                  appearance="secondary"
                />
              </CustomTooltip>
            </div>
          </PopupModal>
        )}

        {isCancelDeliveryVisible && (
          <PopupModal
            direction="left"
            justify="end"
            selfClose={false}
            backdrop
            onClose={() => this.setState({ cancelDeliveryModal: false })}
            visible={this.state.cancelDeliveryModal}
            content={({ closeModal }) => (
              <ModalMessage
                minWidth={300}
                title={localeStore.t('cancel_delivery_message')}
                closeModal={closeModal}
                iconClassName="color-gray2"
                options={this.cancelDeliveryMessageOptions}
              />
            )}
          >
            <Button
              title={localeStore.t('cancel_delivery')}
              onClick={() => this.setState({ cancelDeliveryModal: true })}
              loading={this.state.cancelingDelivery}
              appearance="tertiary"
            />
          </PopupModal>
        )}
        <CancelDeliveryWarningModal
          isOpen={this.state.cancelDeliveryWarningModal}
          closeCancelDeliveryWarningModal={() => this.cancelDeliveryWarningModal(false)}
        />
      </div>
    );
  }
}
